import type {
  ChatMarkdownCommentLinkPressArgs,
  ChatMarkdownHighlightLinkPressArgs,
} from "@sudowrite/shared-chat";
import { useProjectStore } from "src/stores/projectStore";
import { DEFAULT_AGENTIC_CHAT_MODE } from "~shared/constants";
import { navigateToStoryBibleFromChatLink } from "./mentions/navigateToEntityMention";

function documentIdBelongsToLoadedProject(documentId: string | undefined): boolean {
  if (!documentId) return true;
  const contents = useProjectStore.getState().contents;
  if (!Array.isArray(contents)) return true;
  const deletedDocuments = useProjectStore.getState().deletedDocuments;
  const id = String(documentId).trim();
  if (contents.some((item) => item?.id != null && String(item.id).trim() === id)) return true;
  if (
    Array.isArray(deletedDocuments) &&
    deletedDocuments.some((item) => item?.id != null && String(item.id).trim() === id)
  ) {
    return true;
  }
  return false;
}

export function handleWebStoryBibleLinkPress(href: string) {
  navigateToStoryBibleFromChatLink(href);
}

export function handleWebCommentLinkPress(
  args: ChatMarkdownCommentLinkPressArgs,
  activeProjectId?: string | null,
) {
  const { commentId, documentId: targetDocumentId, projectId: projectIdFromLink, deepLinkHref } = args;
  if (!commentId) return;

  const liveProjectRaw = useProjectStore.getState().project?.id;
  const liveProjectTrimmed = typeof liveProjectRaw === "string" ? liveProjectRaw.trim() : "";
  const propProjectTrimmed = typeof activeProjectId === "string" ? activeProjectId.trim() : "";
  const resolvedCurrent = liveProjectTrimmed || propProjectTrimmed;
  const linkProjectTrimmed = typeof projectIdFromLink === "string" ? projectIdFromLink.trim() : "";
  const openOtherBookInNewTab =
    Boolean(linkProjectTrimmed) &&
    Boolean(targetDocumentId) &&
    (!resolvedCurrent || linkProjectTrimmed !== resolvedCurrent);

  if (openOtherBookInNewTab && deepLinkHref) {
    window.open(deepLinkHref, "_blank", "noopener,noreferrer");
    return;
  }
  if (targetDocumentId && !linkProjectTrimmed && !documentIdBelongsToLoadedProject(String(targetDocumentId))) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent("sudowrite:open-comment", {
      detail: { commentId, documentId: targetDocumentId },
    }),
  );
}

export function handleWebManuscriptHighlightLinkPress(args: ChatMarkdownHighlightLinkPressArgs) {
  const { passage, documentId, matchCharOffset } = args;
  void (async () => {
    try {
      const [
        { navigateToManuscriptHighlightFromChat },
        { useSudowriteStore },
        { normalizePreferredManuscriptHighlightLight },
      ] = await Promise.all([
        import("./navigateToManuscriptHighlightFromChat"),
        import("src/stores/sudowriteStore"),
        import("~shared/highlightColors"),
      ]);
      const userData = useSudowriteStore.getState().userData;
      const agenticChatMode = userData?.data?.agenticChatMode?.value ?? DEFAULT_AGENTIC_CHAT_MODE.value;
      const rawPreferred = userData?.data?.preferredManuscriptHighlightLight;
      const preferredHighlightLight = normalizePreferredManuscriptHighlightLight(rawPreferred);
      await navigateToManuscriptHighlightFromChat({
        passage,
        documentId,
        ...(typeof matchCharOffset === "number" ? { matchCharOffset } : {}),
        selectText: agenticChatMode === "write",
        scrollBehavior: "smooth",
        ...(preferredHighlightLight ? { preferredHighlightLight } : {}),
      });
    } catch {
      // non-fatal
    }
  })();
}

export function createWebChatMarkdownLinkHandlers(activeProjectId?: string | null) {
  return {
    onStoryBibleLinkPress: handleWebStoryBibleLinkPress,
    onCommentLinkPress: (args: ChatMarkdownCommentLinkPressArgs) => {
      handleWebCommentLinkPress(args, activeProjectId);
    },
    onManuscriptHighlightLinkPress: handleWebManuscriptHighlightLinkPress,
  };
}
